import {
  recipeConfigSource,
  recipeSchedulingKnobs,
  type JsonObject,
  type SessionCheckpointView,
  type SessionNodeLifetimeView,
  type TrainingSessionRecipeView,
} from "@/api";
import type { TrainingSessionPodMetricsView } from "@/api/trainingEval";
import { formatBranchHash, isFullSha, shortSha } from "@/components/table/branchHash";
import { parseCheckpointTag } from "@/modules/training/public";
import { formatBytes } from "@/utils";
import { MIN_LIFETIME_OPTIONS } from "../submit/submitWizardForm";

// 세션 상세 표면의 순수 파생·포맷 함수. 컴포넌트는 이 함수들만 호출해 얇게 유지한다.

// 체크포인트 태그(`epoch_050`/`step_0100000`)를 budget unit 에 맞춰 "EPOCH 50" / "STEP 100000" 으로
// 표시한다. step 세션에서 "EPOCH step_0100000" 이 되지 않게 한다. 파싱할 수 없는 태그는 그대로 둔다.
// 태그가 없는 항목(카탈로그 등록 정책의 artifact 가 태그 디렉터리 밖)은 고정 문구다. 그래서 번호로
// 읽히지 않는다. 행끼리의 구분은 artifactPathTail 이 맡는다.
export function checkpointDisplayLabel(tag: string | null): string {
  if (tag === null) {
    return "태그 없음";
  }
  const parsed = parseCheckpointTag(tag);
  if (parsed === null) {
    return tag;
  }
  return `${parsed.unit === "step" ? "STEP" : "EPOCH"} ${parsed.value}`;
}

// artifact gs:// URI 의 끝부분(마지막 두 세그먼트, 예 `registered/policy_info.cb6ptx`). 태그 없는 항목을
// 행끼리 구분하는 표기다. 등록 정책의 파일명은 대개 같은 `policy_info.cb6ptx` 라서 파일명만으로는 구분되지 않는다.
export function artifactPathTail(uri: string): string {
  const segments = uri
    .replace(/^[a-z]+:\/\//, "")
    .split("/")
    .filter((segment) => segment !== "");
  return segments.slice(-2).join("/");
}

// 체크포인트 행의 안정 키. 태그를 쓰고, 없으면 artifact 위치를 쓴다. 표 행 id 와 등록 pending 표기가
// 같은 키를 본다. 상류는 둘 다 없는 항목을 만들지 않지만(항목마다 gcs_uri 가 있다) 키는 비우지 않는다.
export function checkpointRowId(
  checkpoint: Pick<SessionCheckpointView, "epoch" | "artifact_uri">,
): string {
  return checkpoint.epoch ?? checkpoint.artifact_uri ?? "untagged";
}

// 체크포인트 크기 표기. 0 은 크기 미상으로 처리한다. 0 B 체크포인트는 없고, 상류가 등록 정책 artifact
// 의 크기 조회에 실패하면 0 을 준다.
export function checkpointSizeLabel(sizeBytes: number | null): string {
  return sizeBytes !== null && sizeBytes > 0 ? formatBytes(sizeBytes) : "크기 미상";
}

// 지표 숫자 표기. 정수는 천 단위 구분, 소수는 4자리(불필요한 0 제거), 매우 크거나 작은 값은 지수로 쓴다.
export function formatMetricNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  const abs = Math.abs(value);
  if (abs < 1e-4 || abs >= 1e6) {
    return value.toExponential(2);
  }
  return String(Number(value.toFixed(4)));
}

// JsonObject(metrics_summary 등)에서 스칼라(숫자·문자·불리언)만 라벨-값으로 평평하게 만든다. 중첩
// 객체·배열·null 은 한 줄 표면에 담기 어려워 건너뛴다.
export type ScalarEntry = { key: string; value: string };

export function scalarEntries(source: JsonObject | null): ScalarEntry[] {
  if (source === null) {
    return [];
  }
  const entries: ScalarEntry[] = [];
  for (const [key, raw] of Object.entries(source)) {
    if (typeof raw === "number") {
      entries.push({ key, value: formatMetricNumber(raw) });
    } else if (typeof raw === "string") {
      entries.push({ key, value: raw });
    } else if (typeof raw === "boolean") {
      entries.push({ key, value: raw ? "true" : "false" });
    }
  }
  return entries;
}

// 진행률(0..1) → 백분율. null 은 "—".
export function formatProgressFraction(fraction: number | null): string {
  if (fraction === null) {
    return "—";
  }
  const clamped = Math.min(Math.max(fraction, 0), 1);
  return `${Math.round(clamped * 100)}%`;
}

// 남은 시간(초) → 사람이 읽는 길이. 음수/null 은 "—".
export function formatEtaSeconds(seconds: number | null): string {
  if (seconds === null || seconds < 0) {
    return "—";
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}시간 ${minutes}분`;
  }
  if (minutes > 0) {
    return `${minutes}분 ${secs}초`;
  }
  return `${secs}초`;
}

// 실행 길이(ms) → 초/분. 음수/null 은 "—".
// training 의 `runtime_ms` 는 결과를 MLflow 에 기록한 구간만 측정하므로 여기에 전달하지 않는다.
// 실제 길이는 started_at/finished_at 과 `wall_t` 지표에서 나온다.
export function formatDurationMs(ms: number | null): string {
  if (ms === null || ms < 0) {
    return "—";
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${Number(seconds.toFixed(1))}초`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}분 ${rest}초`;
}

// 대표 지표 요약. metrics(집계)의 앞 N개를 "k=v"로, 나머지는 "+k"로 줄인다. 비면 "—".
export function topMetricsLabel(metrics: Record<string, number>, limit = 3): string {
  const entries = Object.entries(metrics);
  if (entries.length === 0) {
    return "—";
  }
  const shown = entries
    .slice(0, limit)
    .map(([key, value]) => `${key}=${formatMetricNumber(value)}`);
  const extra = entries.length - shown.length;
  return extra > 0 ? `${shown.join(", ")} +${extra}` : shown.join(", ");
}

// 코드 출처 두 조각. Policy·Results 표의 `Branch@Hash` 셀과 같은 계약이다(`BranchHashCell`).
// 레시피 요약은 응답에 포함된 값만 쓴다. 값이 없으면 null 로 두고, 현재 branch-config 카탈로그에서
// 제출 시점 정보를 추정하지 않는다. sha 는 빌더가 해석한 뒤에만 온다.
export function sessionLearningCore(recipe: TrainingSessionRecipeView | null | undefined): {
  branch: string | null;
  shortHash: string | null;
} {
  const learningCore = recipe?.learning_core;
  if (!learningCore) {
    return { branch: null, shortHash: null };
  }
  return {
    branch:
      learningCore.kind === "branch"
        ? learningCore.ref
        : `${learningCore.kind} · ${learningCore.ref}`,
    // git ref 가 없는 wheel 에는 커밋이 없다. 상류가 관계없는 값을 보내도 붙이지 않는다.
    shortHash: learningCore.kind === "branch" ? shortSha(learningCore.sha) : null,
  };
}

// 위 두 조각을 한 문자열로 합친다. 화면 요약이 쓴다. 셀은 `BranchHashCell` 을 쓴다.
export function sessionBranchLabel(recipe: TrainingSessionRecipeView | null | undefined): string {
  const { branch, shortHash } = sessionLearningCore(recipe);
  return formatBranchHash(branch, shortHash);
}

// 브랜치 제출의 learning-core 를 브랜치와 커밋 두 값으로 나눈다.
//   - ref 가 40-hex 면 커밋 고정 제출이다. 브랜치는 모르고, 커밋은 `sha ?? ref` 다.
//   - 아니면 ref 가 브랜치이고, sha 는 빌더가 해석한 커밋이다(해석 전에는 null).
// 커밋은 full SHA 소문자로만 반환한다. 빈 문자열·접두사·대문자가 그대로 고정 값이 되면 게이트가
// 닫히거나 목록·제안 매칭이 틀린다. 재제출 프리필에서 이 판정을 쓴다.
export function learningCoreRevision(learningCore: { ref: string; sha?: string | null }): {
  branch: string | null;
  sha: string | null;
} {
  const sha = isFullSha(learningCore.sha) ? learningCore.sha!.toLowerCase() : null;
  if (isFullSha(learningCore.ref)) {
    return { branch: null, sha: sha ?? learningCore.ref.toLowerCase() };
  }
  return { branch: learningCore.ref, sha };
}

// 전체 요약 복사도 화면과 같은 구분자를 쓰되 커밋은 원문으로 남긴다.
export function sessionBranchCopyText(
  recipe: TrainingSessionRecipeView | null | undefined,
): string {
  const { branch } = sessionLearningCore(recipe);
  return formatBranchHash(
    branch,
    recipe?.learning_core?.kind === "branch" ? recipe.learning_core.sha : null,
  );
}

export function sessionConfigLabel(
  recipe: TrainingSessionRecipeView | null | undefined,
  hasMlflowRun: boolean,
): string {
  const config = recipe?.config;
  if (config?.name?.trim()) {
    return config.name;
  }
  if (recipeConfigSource(config) === "inline_yaml") {
    return "Inline YAML";
  }
  return hasMlflowRun ? "Config 탭에서 확인" : "—";
}

export function requestedGpuLabel(recipe: TrainingSessionRecipeView | null | undefined): string {
  const resources = recipe?.resources;
  const nodes = resources?.nodes ?? undefined;
  const gpusPerNode = resources?.gpus_per_node ?? undefined;
  if (nodes === undefined && gpusPerNode === undefined) {
    return "—";
  }
  if (nodes !== undefined && gpusPerNode !== undefined) {
    return `${nodes} 노드 · 총 ${nodes * gpusPerNode} GPU`;
  }
  return nodes !== undefined ? `${nodes} 노드` : `노드당 ${gpusPerNode} GPU`;
}

// 제출 시 요청한 스팟 허용의 3-state. false 는 명시적 거부라 "—"(미지정)로 합치지 않는다.
export function allowSpotLabel(recipe: TrainingSessionRecipeView | null | undefined): string {
  const { allowSpot } = recipeSchedulingKnobs(recipe);
  if (allowSpot === null) {
    return "—";
  }
  return allowSpot ? "허용" : "미허용";
}

// 제출 시 요청한 노드 잔여 수명 하한(요구치). 제출 폼과 같은 tier 문구로 읽히게 하고, 폼 밖에서
// 들어온 임의 값은 길이로 표시한다. 0 은 폼 선택지 그대로 "하한 없음", 미지정은 "—" 다.
export function minLifetimeRequestLabel(
  recipe: TrainingSessionRecipeView | null | undefined,
): string {
  const { minLifetimeSeconds: seconds } = recipeSchedulingKnobs(recipe);
  if (seconds === null) {
    return "—";
  }
  const option = MIN_LIFETIME_OPTIONS.find((candidate) => candidate.seconds === seconds);
  return option !== undefined ? option.label : `${formatEtaSeconds(seconds)} 이상`;
}

// duration 필드는 절대 시각이 아니라 남은 길이라서 받는 순간부터 오래된 값이 된다. 수신 시각 기준으로
// 지난 시간만큼 줄여 폴링 없이도 현재 값을 유지한다. eta 와 관측 잔여 수명을 같은 방식으로 처리한다.
export function decayedSeconds(
  seconds: number | null,
  receivedAtMs: number | null,
  nowMs: number,
): number | null {
  if (seconds === null || receivedAtMs === null) {
    return seconds;
  }
  return seconds - (nowMs - receivedAtMs) / 1000;
}

// 관측 노드 잔여 수명 표시. 숫자는 measured 에서만 온다. uncapped(상한 없는 on-demand)는 경고 대상이
// 아닌 정상 상태라 "제한 없음" 이다. unknown·unavailable 은 "모름" 이다. undefined 는 rich detail 을
// 아직 로드하지 않은 상태다.
export function nodeLifetimeRemainingLabel(
  lifetime: SessionNodeLifetimeView | undefined,
  decayedRemainingSeconds: number | null,
): string {
  if (lifetime === undefined || lifetime.state === "unknown" || lifetime.state === "unavailable") {
    return "—";
  }
  if (lifetime.state === "uncapped") {
    return "제한 없음";
  }
  return formatEtaSeconds(decayedRemainingSeconds);
}

// ETA 경고 판정. 비교 대상은 관측 잔여 수명이다. 요구치(min_remaining_lifetime_seconds)를 쓰면
// "요청했으니 안전"으로 항상 통과한다. measured 외 상태는 비교할 수 없어 경고하지 않는다.
export function nodeLifetimeWarningActive(
  lifetime: SessionNodeLifetimeView | undefined,
  decayedEtaSeconds: number | null,
  decayedRemainingSeconds: number | null,
): boolean {
  return (
    lifetime !== undefined &&
    lifetime.state === "measured" &&
    decayedEtaSeconds !== null &&
    decayedRemainingSeconds !== null &&
    decayedEtaSeconds > decayedRemainingSeconds
  );
}

// ── 실행 중 리소스(pod·GPU 순간 지표) ─────────────────────────────────────────

// pod 표시 이름. 분산 학습의 역할과 순번이 있으면 그것을 쓴다. 예) `master 0`. 없으면 pod 이름이다.
export function sessionPodLabel(
  pod: Pick<TrainingSessionPodMetricsView, "pod" | "role" | "rank">,
): string {
  if (pod.role === null) {
    return pod.pod;
  }
  return pod.rank === null ? pod.role : `${pod.role} ${pod.rank}`;
}

// "사용 / 상한" 한 쌍. 상한이 없으면 사용량만, 사용량이 없으면 그 자리가 "—" 다. 둘 다 없으면 "—" 다.
function usagePairLabel(used: string | null, limit: string | null): string {
  if (limit === null) {
    return used ?? "—";
  }
  return `${used ?? "—"} / ${limit}`;
}

// CPU 코어 수. 정수는 그대로, 소수는 1자리다. 예) `16`, `6.2`
function cpuCoresText(cores: number | null): string | null {
  if (cores === null || !Number.isFinite(cores)) {
    return null;
  }
  return Number.isInteger(cores) ? String(cores) : cores.toFixed(1);
}

// 예) `6.2 / 16 코어`. 상한이 없는 pod 는 `6.2 코어` 다.
export function cpuUsageLabel(used: number | null, limit: number | null): string {
  const label = usagePairLabel(cpuCoresText(used), cpuCoresText(limit));
  return label === "—" ? label : `${label} 코어`;
}

// 예) `41.0 GB / 120 GB`
export function memoryUsageLabel(usedBytes: number | null, limitBytes: number | null): string {
  return usagePairLabel(
    usedBytes === null ? null : formatBytes(usedBytes, "—"),
    limitBytes === null ? null : formatBytes(limitBytes, "—"),
  );
}

// GPU 사용률(0~100)을 막대와 숫자가 함께 쓰는 정수 퍼센트로 바꾼다. 측정값이 없으면 null 이다.
export function gpuUtilPercent(utilPct: number | null): number | null {
  if (utilPct === null || !Number.isFinite(utilPct)) {
    return null;
  }
  return Math.round(Math.min(100, Math.max(0, utilPct)));
}

// 순간 지표가 얼마나 지난 값인지 알린다. 폴링이 실패하면 표시 중인 값이 지난 값임을 말한다.
export function resourceFreshnessLabel(
  fetchedAtMs: number,
  nowMs: number,
  refreshFailed: boolean,
): string {
  const seconds = Math.max(0, Math.round((nowMs - fetchedAtMs) / 1000));
  if (refreshFailed) {
    return `갱신 실패 · ${seconds}초 전 값`;
  }
  return seconds < 5 ? "방금 갱신" : `${seconds}초 전 갱신`;
}
